import { z } from 'zod';

/** The editor reports activity this often while the user works (visible tab, recent input). */
export const ACTIVITY_HEARTBEAT_MS = 60_000;
/** A pause longer than this starts a new work session. */
export const WORK_SESSION_GAP_MS = 5 * 60_000;
/** Someone counts as working in a project right now this long after their last heartbeat. */
export const ACTIVE_NOW_MS = 2 * ACTIVITY_HEARTBEAT_MS + 30_000;

/** A person working in a project right now. */
export const activePersonSchema = z.object({ id: z.string(), displayName: z.string() });
export type ActivePerson = z.infer<typeof activePersonSchema>;

export const heartbeatRequestSchema = z.object({
  projectId: z.uuid().nullable().default(null),
  /** Touch screen: tells iPads (which report themselves as Macs) apart. */
  touch: z.boolean().default(false),
});
export type HeartbeatRequest = z.input<typeof heartbeatRequestSchema>;

const personSchema = z.object({ id: z.string(), displayName: z.string(), email: z.string() });

export const activityUserSchema = personSchema.extend({
  lastActiveAt: z.string().nullable(),
  lastDevice: z.string().nullable(),
  lastLocation: z.string().nullable(),
  lastIp: z.string().nullable(),
  workMinutes7: z.number(),
  workMinutes30: z.number(),
  logins30: z.number().int(),
  failed30: z.number().int(),
  /** Working in the editor right now, and in which project. */
  online: z.boolean(),
  activeProject: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type ActivityUser = z.infer<typeof activityUserSchema>;

export const activityEntrySchema = z.object({
  id: z.string(),
  kind: z.enum(['login', 'logout', 'login-failed', 'work']),
  at: z.string(),
  /** Null for failed attempts with an unknown e-mail address. */
  user: personSchema.nullable(),
  email: z.string().nullable(),
  ip: z.string().nullable(),
  location: z.string().nullable(),
  device: z.string().nullable(),
  /** Work sessions only. */
  durationMinutes: z.number().nullable(),
  until: z.string().nullable(),
  project: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type ActivityEntry = z.infer<typeof activityEntrySchema>;

export const adminActivitySchema = z.object({
  users: z.array(activityUserSchema),
  entries: z.array(activityEntrySchema),
  retentionDays: z.number().int(),
});
export type AdminActivity = z.infer<typeof adminActivitySchema>;
